import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Icon } from 'tea-component';
import moment, { Moment } from 'moment';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLocale } from 'umi';
import RangePicker from '@home/components/RangePicker';
import GameImage from '@home/components/GameImage';
import type { IntelligenceReportDataInfo } from '../../type';
import { getDefaultDisplayTimeRange } from '../ParentDateFilter/_config';
import './index.scss';

export interface ISelectedReportItemProps {
  data: IntelligenceReportDataInfo;
  /** 受控日期范围（来自父组件 reportDateRanges） */
  dateRange?: { startTime: string; endTime: string };
  onRemove?: (data: IntelligenceReportDataInfo) => void;
  onDateChange?: (data: IntelligenceReportDataInfo, startTime: string, endTime: string) => void;
}

const SelectedReportItem: React.FC<ISelectedReportItemProps> = (props) => {
  const { data, dateRange: controlledDateRange, onRemove, onDateChange } = props;
  const locale = getLocale();
  const isEn = locale === LANGUAGE_EN;

  const fallbackDateRange = useMemo(() => {
    if (data.studio_start_time && data.studio_end_time) {
      return [
        moment(data.studio_start_time),
        moment(data.studio_end_time),
      ] as [Moment, Moment];
    }
    return getDefaultDisplayTimeRange();
  }, [data.studio_start_time, data.studio_end_time]);

  const resolvedDateRange = useMemo(() => {
    if (controlledDateRange?.startTime && controlledDateRange?.endTime) {
      return [
        moment(controlledDateRange.startTime),
        moment(controlledDateRange.endTime),
      ] as [Moment, Moment];
    }
    return fallbackDateRange;
  }, [
    controlledDateRange?.startTime,
    controlledDateRange?.endTime,
    fallbackDateRange,
  ]);

  const [dateRange, setDateRange] = useState<[Moment, Moment]>(resolvedDateRange);

  useEffect(() => {
    setDateRange(resolvedDateRange);
  }, [resolvedDateRange]);

  // 无受控值时，挂载后同步默认日期到父组件（仅执行一次）
  const syncedOnMountRef = useRef(false);
  useEffect(() => {
    if (syncedOnMountRef.current) return;
    if (controlledDateRange?.startTime || !onDateChange) return;
    syncedOnMountRef.current = true;
    const startTime = fallbackDateRange[0].format('YYYY-MM-DD HH:mm:ss');
    const endTime = fallbackDateRange[1].format('YYYY-MM-DD HH:mm:ss');
    onDateChange(data, startTime, endTime);
  }, [controlledDateRange?.startTime, onDateChange, fallbackDateRange, data]);

  const title = useMemo(() => {
    return getLanguage(isEn, data.title_en, data.title) || '-';
  }, [isEn, data.title_en, data.title]);

  const handleDateChange = (value: [Moment, Moment]) => {
    setDateRange(value);
    if (onDateChange) {
      const startTime = value[0].format('YYYY-MM-DD HH:mm:ss');
      const endTime = value[1].format('YYYY-MM-DD HH:mm:ss');
      onDateChange(data, startTime, endTime);
    }
  };

  const handleRemove = () => {
    onRemove?.(data);
  };

  return (
    <div className="dbh-selected-report-item">
      <div className="dbh-selected-report-item-content">
        {/* 封面图 */}
        <div className="dbh-selected-report-item-cover">
          <GameImage
            src={data.cover === 'error' ? '' : data.cover}
            alt={title}
            entityType="report"
          />
        </div>

        {/* 标题和删除按钮 */}
        <div className="dbh-selected-report-item-info">
          <div className="dbh-selected-report-item-header">
            <div className="dbh-selected-report-item-title">{title}</div>
            <Icon
              type="dismiss"
              className="dbh-selected-report-item-remove"
              onClick={handleRemove}
            />
          </div>
        </div>
      </div>

      {/* 日期范围选择器 */}
      <div className="search-item search-item-date">
        <RangePicker
          value={dateRange}
          onChange={handleDateChange}
          type="date"
          typeList={['date']}
          showSelectType={false}
          showShortcut={false}
          language={isEn ? 'en' : 'cn'}
          syncInput={true}
        />
      </div>
    </div>
  );
};

export default SelectedReportItem;
